import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { resetPassword, logout, clearError } from '../../store/authSlice';
import { toast } from 'react-toastify';
import { COLORS } from '../../shared/colors';
import { REGEX } from '../../shared/regex';
import { BUTTON_TEXT } from '../../shared/appConstants';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  LockOutlined,
  Visibility,
  VisibilityOff,
  CheckCircle
} from '@mui/icons-material';
import AuthFormShell from './AuthFormShell';

const ResetPasswordForm = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      toast.error('Invalid or expired reset token.');
      navigate('/login');
    }
  }, [token, navigate]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const validatePassword = (password) => {
    if (password.includes(' ')) {
      return { isValid: false, error: t('resetPassword.spaceError') };
    }
    if (!REGEX.passwordLength.test(password)) {
      return { isValid: false, error: t('resetPassword.minLengthError') };
    }
    if (!REGEX.passwordUppercase.test(password)) {
      return { isValid: false, error: t('resetPassword.uppercaseError') };
    }
    if (!REGEX.passwordDigit.test(password)) {
      return { isValid: false, error: t('resetPassword.digitError') };
    }
    if (!REGEX.passwordSpecial.test(password)) {
      return { isValid: false, error: t('resetPassword.specialCharError') };
    }
    return { isValid: true };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.error);
      return;
    }

    if (password !== confirmPassword) {
      setError(t('resetPassword.mismatchError'));
      return;
    }

    setIsLoading(true);
    
    try {
      await dispatch(resetPassword({ token, new_password: password })).unwrap();
      // Logout any existing session
      dispatch(logout());
      toast.success('Password reset successfully! Please login with your new password.');
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err || t('resetPassword.resetError'));
      toast.error('Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <AuthFormShell maxWidth={560} cardPaddingX={6}>
        <Box sx={{ width: '100%', maxWidth: 560, px: 0, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 60, color: '#082B72', mb: 2 }} />
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: '#082B72', mb: 1, fontSize: { xs: '30px', sm: '36px' } }}
          >
            {t('resetPassword.successHeading')}
          </Typography>
          <Typography variant="h6" sx={{ color: '#80899A', fontWeight: 400, mb: 3, fontSize: { xs: '18px', sm: '24px' } }}>
            {t('resetPassword.successMessage')}
          </Typography>
        </Box>
      </AuthFormShell>
    );
  }

  return (
    <AuthFormShell maxWidth={560} minHeight={error?550:490} contentTop={80} cardPaddingX={6}>
      <Box sx={{ width: '100%', maxWidth: 560, px: 0 }}>
        <Box sx={{ textAlign: 'center', mt: 0.5, mb: 5 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              color: '#082B72',
              mb: 1,
              fontSize: { xs: '30px', sm: '36px' }
            }}
          >
            {t('resetPassword.title')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#80899A',
              fontWeight: 400,
              fontSize: { xs: '18px', sm: '24px' }
            }}
          >
            {t('resetPassword.subtitle')}
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              fullWidth
              variant="outlined"
              label={t('resetPassword.newPassword')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined className="input-icon-red" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              className="red-outlined-input"
              sx={{ mb: 4 }}
            />
            
            <TextField
              fullWidth
              variant="outlined"
              label={t('resetPassword.confirmNewPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined className="input-icon-red" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              className="red-outlined-input"
              sx={{ mb: 4 }}
            />
            
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    color: COLORS.alert.error
                  }
                }}
              >
                {error}
              </Alert>
            )}
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                height: 52,
                bgcolor: '#082B72',
                borderRadius: '4px',
                fontSize: '20px',
                fontWeight: 400,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#061f53',
                  boxShadow: 'none'
                },
                '&:disabled': {
                  bgcolor: '#5f76a8',
                  color: '#ffffff'
                },
                transition: 'none'
              }}
            >
              {isLoading ? BUTTON_TEXT.RESETTING_PASSWORD : BUTTON_TEXT.RESET_PASSWORD}
            </Button>
          </Box>
      </Box>
    </AuthFormShell>
  );
};

export default ResetPasswordForm;
